// @ts-strict-ignore
import { LoadConnectionTextureProcessor } from '@3d-scene/processor/load-connection-texture.processor';
import { Dictionary } from 'typescript-collections';
import { Connection } from '@3d-scene/model3d/connection';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { DefinedMetaElementInfoPropertyDef } from '@project/constants/defined-meta-element-info-property-def';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { StringUtils } from '../../util/string-utils';
import { ArrayUtils } from '../../util/array-utils';
import { LabelingUtils } from '@3d-scene/model3d/util/labeling-utils';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';

class ArticleConnectionData {
  public articleGuid: string;
  public connectionNames: string[] = [];

  public constructor(articleGuid: string, connectionNames: string[]) {
    this.articleGuid = articleGuid;
    this.connectionNames = connectionNames;
  }
}

export class BaseImageConnectionsOperation {
  private _imageHashToArticleConnectionDatas: Dictionary<string, ArticleConnectionData[]>;

  constructor(
    connectionCubes: Connection[],
    private _loadTextureProcessor: LoadConnectionTextureProcessor,
    private containerService: ContainerService,
    private labelingUtils: LabelingUtils
  ) {
    this.storeImageHashes(connectionCubes);
  }

  protected storeImageHashes(connectionCubes: Connection[]): void {
    this._imageHashToArticleConnectionDatas = new Dictionary<string, ArticleConnectionData[]>();
    for (const connectionCube of connectionCubes) {
      const container: ArticleContainer = connectionCube.articleContainer;
      const articleGuid: string = container.articleGUID;
      const connectionName: string = connectionCube.connectionName;
      const imageHash: string = ArticleUsageUtils.getDefinedMetaElementInfoPropertyStringValue(
        container.articleUsage,
        connectionName,
        DefinedMetaElementInfoPropertyDef.CUSTOM_IMAGE_HASH
      );
      const articleConnectionDatas: ArticleConnectionData[] = this._imageHashToArticleConnectionDatas.getValue(imageHash);
      let articleConnectionData: ArticleConnectionData = this.findArticleConnectionDataInSource(articleGuid, articleConnectionDatas);
      if (!articleConnectionData) {
        articleConnectionData = new ArticleConnectionData(articleGuid, [connectionName]);
      }

      if (!articleConnectionDatas) {
        this._imageHashToArticleConnectionDatas.setValue(imageHash, [articleConnectionData]);
      } else {
        if (articleConnectionDatas.indexOf(articleConnectionData) === -1) {
          articleConnectionDatas.push(articleConnectionData);
        } else {
          articleConnectionData.connectionNames.push(connectionName);
        }
      }
    }
  }

  private findArticleConnectionDataInSource(targetGuid: string, source: ArticleConnectionData[]): ArticleConnectionData {
    if (ArrayUtils.isEmpty(source)) {
      return null;
    }
    for (const connectionData of source) {
      if (connectionData.articleGuid === targetGuid) {
        return connectionData;
      }
    }
    return null;
  }

  public getAllProcessedArticleGuids(): string[] {
    const result: string[] = [];
    for (const articleConnectionDatas of this._imageHashToArticleConnectionDatas.values()) {
      for (const articleConnectionData of articleConnectionDatas) {
        const articleGuid: string = articleConnectionData.articleGuid;
        if (result.indexOf(articleGuid) < 0) {
          result.push(articleGuid);
        }
      }
    }
    return result;
  }

  private getAllProcessedConnectionCubes(): Connection[] {
    let connectionCubes: Connection[] = [];
    for (const imageHash of this._imageHashToArticleConnectionDatas.keys()) {
      const cubesWithSameImage: Connection[] = this.findProcessedConnectionCubesByImageHash(imageHash);
      connectionCubes = connectionCubes.concat(cubesWithSameImage);
    }
    return connectionCubes;
  }

  private findProcessedConnectionCubesByImageHash(imageHash: string): Connection[] {
    const connectionCubes: Connection[] = [];
    for (const articleConnectionData of this._imageHashToArticleConnectionDatas.getValue(imageHash)) {
      const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(articleConnectionData.articleGuid);
      if (!articleContainer) {
        continue;
      }
      for (const connectionName of articleConnectionData.connectionNames) {
        const connectionCube: Connection = articleContainer.findFirstConnectionByName(connectionName);
        if (connectionCube) {
          connectionCubes.push(connectionCube);
        }
      }
    }
    return connectionCubes;
  }

  protected removeImages(): void {
    const processedConnectionCubes: Connection[] = this.getAllProcessedConnectionCubes();
    this._loadTextureProcessor.cleanConnectionImageCache(processedConnectionCubes);
    ConnectionInfoUtils.removeImagesFromConnectionCubes(processedConnectionCubes);
    this._loadTextureProcessor.loadAndApplyImagesToConnections(processedConnectionCubes);
  }

  protected cleanCacheAndRestoreImages(imageHash: string = null): void {
    this.restoreCustomImageHashProperty();
  }

  protected restoreImages(cleanCache: boolean = true, imageHash: string = null): void {
    const processedConnectionCubes: Connection[] = this.getAllProcessedConnectionCubes();
    if (cleanCache) {
      this._loadTextureProcessor.cleanConnectionImageCache(processedConnectionCubes);
    }

    if (!imageHash) {
      this.restoreCustomImageHashProperty();
    } else {
      ConnectionInfoUtils.updateImageHashForConnections(imageHash, processedConnectionCubes);
    }
    this._loadTextureProcessor.loadAndApplyImagesToConnections(this.labelingUtils.collectAllMergedTextureConnections(processedConnectionCubes));
  }

  private restoreCustomImageHashProperty(): void {
    for (const imageHash of this._imageHashToArticleConnectionDatas.keys()) {
      const connectionCubesWithSameImage: Connection[] = this.findProcessedConnectionCubesByImageHash(imageHash);
      if (StringUtils.isEmptyOrLiteralNull(imageHash) || !imageHash) {
        // remove image from connection, if there was no image used before
        ConnectionInfoUtils.removeImagesFromConnectionCubes(connectionCubesWithSameImage);
      } else {
        ConnectionInfoUtils.updateImageHashForConnections(imageHash, connectionCubesWithSameImage);
      }
    }
  }
}
